// Inventario del Portal Admin VentasLuga: banda de periodo y explorer filtros · tabla de SKUs · detalle.
// El periodo va en `?periodo=`; lista y detalle van 3fr/2fr, con el detalle de al menos 420px.
import { useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { RoleAssignmentInfoLocal } from '@/api/types/MeLocal';
import { useAuth } from '@/hooks/useAuth';
import { ErrorState } from '@/kit';
import { mxDayOffset } from '@/lib/dateHelpers';
import { ScreenControls } from '@/shared/patterns/AppShell/screenControls';
import { MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenFrame } from '@/shared/patterns/ScreenFrame';
import {
    DEFAULT_REPORT_PERIOD,
    PeriodBand,
    parseReportPeriod,
    type ReportPeriod,
} from '@/shared/patterns/PeriodBand';
import {
    useCardStock,
    useInventoryList,
    useRetailerStores,
    useRetailerZones,
} from './api/useInventoryList';
import { NewItemButton } from './components/catalog/NewItemDrawer';
import { ReconcileMenu } from '../reconciliation/components/ReconcileButton';
import { RetailerIntegrationLock } from '../integration/components/IntegrationLock';
import { InventoryDetail, type InventoryDetailContext } from './components/InventoryDetail';
import { InventoryFiltersPanel } from './components/InventoryFiltersPanel';
import { InventoryContextControls } from './components/InventoryNotices';
import { InventoryStatsBar } from './components/InventoryStatsBar';
import { InventoryTable } from './components/InventoryTable';
import { LIST_PROMOTIONS_UNAVAILABLE } from './lib/inventoryPromotions';
import { NoSource } from '@/shared/patterns/NoSource';
import { OpenTransfersButton } from './components/movements/OpenTransfers';
import { storeZones } from './lib/inventoryDetail';
import {
    bucketCounts,
    buildInventoryRows,
    DEFAULT_SORT,
    EMPTY_FILTERS,
    initialFilters,
    nextSort,
    type InventoryFilters,
} from './lib/inventoryList';
import { inventoryRetailerNotice, resolveInventoryRetailer } from './lib/inventoryRetailer';
import { useInventoryTable } from './lib/inventoryTableModel';

const EXPLORER_SPLIT = { list: 'minmax(0,3fr)', detail: 'minmax(420px,2fr)' };

/** Ventas y cobertura siguen el periodo; el stock no. */
const STOCK_NOTE = 'el stock es al momento';

/** El nombre de la cadena del scope RBAC; sin nombre, su id. */
function tenantNameOf(
    assignments: readonly RoleAssignmentInfoLocal[],
    retailerId: string | null,
): string {
    const assignment = assignments.find((a) => a.scope === 'LEVEL_1' && a.scopeRef === retailerId);
    return assignment?.scopeRefName ?? retailerId ?? '—';
}

function asError(error: unknown): Error | null {
    if (!error) return null;
    if (error instanceof Error) return error;
    return new Error(typeof error === 'string' ? error : 'Error desconocido');
}

/** `tab` es el deep link de la ruta: `critico` (Home VL) abre con la tarjeta «Stock bajo» activa. */
export function InventarioPage({ tab }: { tab?: string } = {}) {
    const { roleAssignments } = useAuth();
    const { retailerId } = useMemo(
        () => resolveInventoryRetailer(roleAssignments),
        [roleAssignments],
    );
    return (
        <RetailerIntegrationLock retailerId={retailerId}>
            <InventarioScreen tab={tab} />
        </RetailerIntegrationLock>
    );
}

function InventarioScreen({ tab }: { tab?: string }) {
    const { user, roleAssignments } = useAuth();
    const retailer = useMemo(() => resolveInventoryRetailer(roleAssignments), [roleAssignments]);
    const fixedRetailerId = retailer.retailerId;
    const retailerProblem = retailer.problem;

    const navigate = useNavigate();
    const { periodo } = useSearch({ from: '/_backoffice/retail-admin/$section' });
    const period = parseReportPeriod(periodo);
    const [storeId, setStoreId] = useState('');
    const [filters, setFilters] = useState<InventoryFilters>(() => initialFilters(tab));
    const [sort, setSort] = useState(DEFAULT_SORT);
    const [selectedSku, setSelectedSku] = useState<string | null>(null);
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);

    const list = useInventoryList(period, storeId || undefined);
    const stores = useRetailerStores(fixedRetailerId);
    const storeList = useMemo(() => stores.data ?? [], [stores.data]);
    const zones = useRetailerZones(fixedRetailerId);
    const storeIds = useMemo(() => storeList.map((s) => s.storeId), [storeList]);
    const reconcileStores = useMemo(
        () => storeList.map((s) => ({ storeId: s.storeId, name: s.name })),
        [storeList],
    );
    const cards = useCardStock(fixedRetailerId, storeIds);

    const rows = useMemo(
        () =>
            buildInventoryRows(list.data?.items ?? [], cards.data, storeIds, storeId || undefined),
        [list.data, cards.data, storeIds, storeId],
    );
    const { table, pageRows, filteredCount } = useInventoryTable({
        rows,
        filters,
        sort,
        onSortChange: setSort,
    });
    // Sin SKU elegido queda abierto el primero visible; la ficha elegida se queda aunque un filtro la saque.
    const activeSku = selectedSku ?? pageRows.at(0)?.sku ?? null;
    const selectedRow = rows.find((r) => r.sku === activeSku) ?? null;
    const today = mxDayOffset(0);
    const detailCtx = useMemo<InventoryDetailContext>(
        () => ({
            period,
            storeId: storeId || undefined,
            stores: new Map(storeList.map((s) => [s.storeId, { code: s.code, name: s.name }])),
            zones: storeZones(storeList, zones.data ?? []),
            retailerId: fixedRetailerId,
            today,
        }),
        [period, storeId, storeList, zones.data, fixedRetailerId, today],
    );

    const changePeriod = (next: ReportPeriod) => {
        void navigate({
            to: '/retail-admin/$section',
            params: { section: 'inventario' },
            search: (prev) => ({
                ...prev,
                periodo: next === DEFAULT_REPORT_PERIOD ? undefined : next,
            }),
            replace: true,
        });
    };

    const tenantName = tenantNameOf(roleAssignments, fixedRetailerId);
    const error = asError(list.error);
    const showCardsError = cards.isError && filters.kinds.includes('CARD');

    return (
        <ScreenFrame>
            <ScreenControls>
                <InventoryContextControls
                    tenantName={tenantName}
                    adminName={user?.displayName ?? '—'}
                />
            </ScreenControls>
            <PeriodBand period={period} onPeriodChange={changePeriod} note={STOCK_NOTE} />
            {retailerProblem === null ? null : (
                <div
                    role="alert"
                    className="flex-none border-b border-warning-soft-border bg-warning-soft px-3.5 py-1.5 text-[11px] text-warning"
                >
                    {inventoryRetailerNotice(retailerProblem)}
                </div>
            )}
            <MasterDetailExplorer
                storageKey="retail-admin-inventario"
                split={EXPLORER_SPLIT}
                filtersCollapsed={filtersCollapsed}
                filters={
                    <InventoryFiltersPanel
                        filters={filters}
                        onChange={setFilters}
                        storeId={storeId}
                        onStoreChange={setStoreId}
                        stores={storeList}
                        onReset={() => {
                            setFilters(EMPTY_FILTERS);
                            setStoreId('');
                        }}
                        collapsed={filtersCollapsed}
                        onToggleCollapsed={() => {
                            setFiltersCollapsed((value) => !value);
                        }}
                    />
                }
                list={
                    <>
                        <InventoryStatsBar
                            counts={list.data ? bucketCounts(rows) : null}
                            active={filters.bucket}
                            onChange={(bucket) => {
                                setFilters({ ...filters, bucket });
                            }}
                        />
                        <div className="flex flex-none flex-wrap items-center gap-x-3 gap-y-1 border-b border-border/50 px-3 py-1">
                            {list.data?.promotionsUnavailable ? (
                                <span className="text-[10.5px] text-muted-foreground">
                                    Promos: <NoSource reason={LIST_PROMOTIONS_UNAVAILABLE} />
                                </span>
                            ) : null}
                            {showCardsError ? (
                                <span className="text-[10.5px] text-danger">
                                    No se pudo leer el stock de tarjetas PCF.
                                </span>
                            ) : null}
                            <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                                <ReconcileMenu
                                    retailerId={fixedRetailerId}
                                    stores={reconcileStores}
                                    onComplete={() => {
                                        void list.refetch();
                                    }}
                                />
                                <OpenTransfersButton stores={detailCtx.stores} />
                                <NewItemButton
                                    retailerId={fixedRetailerId}
                                    onCreated={setSelectedSku}
                                />
                            </span>
                        </div>
                        {error ? (
                            <div className="min-h-0 flex-1 overflow-auto">
                                <ErrorState
                                    className="m-3"
                                    title="No se pudo cargar el inventario"
                                    description={error.message}
                                    onRetry={() => {
                                        void list.refetch();
                                    }}
                                />
                            </div>
                        ) : (
                            <InventoryTable
                                table={table}
                                pageRows={pageRows}
                                filteredCount={filteredCount}
                                sort={sort}
                                onSortToggle={(column) => {
                                    setSort(nextSort(sort, column));
                                }}
                                selectedSku={activeSku}
                                onSelect={setSelectedSku}
                                isLoading={list.isPending}
                                period={period}
                            />
                        )}
                    </>
                }
                detail={<InventoryDetail row={selectedRow} ctx={detailCtx} />}
            />
        </ScreenFrame>
    );
}
